import { cloneDeep, isEqual } from "lodash-es"

interface UndoHistoryOptions<T> {
  /** 读取当前状态快照（需返回可被深拷贝的纯数据） */
  getSnapshot: () => T
  /** 把快照写回状态 */
  applySnapshot: (snapshot: T) => void
  /** 连续变更合并为一条记录的间隔（ms），避免输入时每个字符一条记录 */
  debounce?: number
  /** 最多保留的历史条数 */
  limit?: number
}

export interface UndoHistory {
  /** 以当前状态为基线清空历史（数据加载 / 放弃修改后调用） */
  reset: () => void
  /** 通知状态发生了变化，由调用方在变更监听中调用 */
  record: () => void
  undo: () => boolean
  redo: () => boolean
  canUndo: () => boolean
  canRedo: () => boolean
}

/**
 * 基于快照的撤销 / 重做历史
 * 与具体 store 无关，由调用方提供快照读取与回写方法
 */
export function createUndoHistory<T>({
  getSnapshot,
  applySnapshot,
  debounce = 400,
  limit = 50,
}: UndoHistoryOptions<T>): UndoHistory {
  let past: T[] = []
  let future: T[] = []
  let present: T | null = null
  let timer: ReturnType<typeof setTimeout> | null = null
  let applying = false

  const take = () => cloneDeep(getSnapshot())

  const cancelPending = () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }

  // 把挂起的变更落为一条历史记录
  const commit = () => {
    cancelPending()
    if (present === null) {
      return
    }

    const next = take()

    if (isEqual(next, present)) {
      return
    }

    past.push(present)
    if (past.length > limit) {
      past = past.slice(past.length - limit)
    }
    future = []
    present = next
  }

  const apply = (snapshot: T) => {
    applying = true
    try {
      applySnapshot(cloneDeep(snapshot))
    } finally {
      applying = false
    }
    present = snapshot
  }

  return {
    reset() {
      cancelPending()
      past = []
      future = []
      present = take()
    },

    record() {
      if (applying || present === null) {
        return
      }
      cancelPending()
      timer = setTimeout(commit, debounce)
    },

    undo() {
      commit()
      const prev = past.pop()

      if (prev === undefined || present === null) {
        return false
      }
      future.push(present)
      apply(prev)
      return true
    },

    redo() {
      commit()
      const next = future.pop()

      if (next === undefined || present === null) {
        return false
      }
      past.push(present)
      apply(next)
      return true
    },

    canUndo: () => past.length > 0 || timer !== null,
    canRedo: () => future.length > 0,
  }
}
